import { IContext } from "@src/context";
import "./index.css";
import searchDataHelper, {
  IRCity,
  IRLine,
  IRPrefecture,
  IRRegion,
  IRStation,
} from "./searchDataHelper";
import popupHelper, { ESearchPopupType, IPosition } from "./popupHelper";
import { defineMessages } from "@formatjs/intl";
import { handleClassByCondition } from "@src/utils/dom";
import singleUrlHelper from "@src/utils/url";
import { getPageCategory } from "@src/utils/helper";
import { anaClickSearch } from "./analytic";
import { mergeCityGroupName } from "./mergeCityGroupName";

export const messages = defineMessages({
  allJapan: {
    id: "SearchPopup.allJapan",
    defaultMessage: "All Japan",
  },
  oArea: {
    id: "SearchPopup.oArea",
    defaultMessage: "Other Districts",
  },
  area: {
    id: "SearchPopup.area",
    defaultMessage: "Area",
  },
  trainLine: {
    id: "SearchPopup.trainLine",
    defaultMessage: "Trainline",
  },
});

enum EEventType {
  clear = "clear", // close popup
  go = "go", // go leaf/node
  select = "select", // select item
  search = "search", // open link
}
export enum EAreaType {
  region = "1",
  prefecture = "2",
  city = "3",
  line = "4",
  station = "5",
}

export const AreaValueToTypeMap = {
  "1": "regions",
  "2": "prefectures",
  "3": "cities",
  "4": "lines",
  "5": "stations",
};

const SHOW_SEARCH_TYPE = [EAreaType.city, EAreaType.line, EAreaType.station];
interface ISearchPopupDataset extends DOMStringMap {
  event: EEventType;
  eventPayloadType?: EAreaType;
  eventPayloadId?: string;
  eventPayloadName?: string;
  eventPayloadUrl?: string;
  eventPayloadCombineId?: string; //sp id
  eventPayloadCount?: string;
}
const CONTAINER = "container-search-popup";

const CONTAINER_CONTENT = "container-search-popup-content";
const CONTAINER_CONTENT_NOT_ROOT = "container-search-popup-content__not__root";

const SEARCH_WRAP = "container-search-popup-search-wrap";
const SEARCH_WRAP_SHOW = "container-search-popup-search-wrap__show";

const ROOT_LIST_CONTAINER = "container-search-popup-left";
const NODE_LIST_CONTAINER = "container-search-popup-detail-node";
const ROOT_ID = "root";

const POPUP_ID = "searchPopup";

const HokkaidoId = "1";
const HokkaidoType = EAreaType.prefecture;

const PAGE_CATEGORY = getPageCategory();

export function SearchPopup(context: IContext) {
  document.querySelectorAll(`.${CONTAINER}`)?.forEach((selectEle) => {
    selectEle.addEventListener("click", function (this: HTMLElement, e: Event) {
      const sourceEle = e.target as HTMLElement;
      const dataSet = sourceEle.dataset as ISearchPopupDataset;
      if (!dataSet.event) return;
      switch (dataSet.event) {
        case EEventType.search:
          goSearch();
          break;
        case EEventType.clear:
          clearState();
          break;
        case EEventType.go: {
          const { eventPayloadType, eventPayloadId, eventPayloadName } =
            dataSet;
          if (!eventPayloadType || !eventPayloadId || !eventPayloadName) return;
          goList({
            type: eventPayloadType,
            id: eventPayloadId,
            name: eventPayloadName,
          });
          break;
        }
        case EEventType.select: {
          const {
            eventPayloadId,
            eventPayloadUrl,
            eventPayloadCount,
            resourceType,
          } = dataSet;
          if (!eventPayloadId) return;
          popupHelper.updateSelected(eventPayloadId, {
            code: eventPayloadId,
            url: eventPayloadUrl ?? "",
            count: eventPayloadCount ?? "0",
            resourceType,
          });
          renderSearchCount();
          break;
        }
      }
    });
  });

  document.addEventListener("click", function (e: Event) {
    const sourceEle = e.target as HTMLElement;
    const dataSet = sourceEle.dataset;
    if (dataSet.popupId == POPUP_ID && dataSet.id) {
      popupHelper.type = dataSet.id as ESearchPopupType;
      //
      initSearchPopup();
    }
  });

  function initSearchPopup() {
    // new popupHelper

    const jsonPromise = searchDataHelper.getJsonApiPromise();
    if (!jsonPromise) return;
    jsonPromise.finally(() => {
      // go root when call initSearchPopup && end of the fetch api
      goList({
        id: ROOT_ID,
        name: context.intl.formatMessage(messages.allJapan),
        type: EAreaType.region,
      });
    });
  }

  function goSearch() {
    anaClickSearch();
    const url = popupHelper.getSearchUrl();
    const sourcePath =
      PAGE_CATEGORY === "parking"
        ? singleUrlHelper.getParkingPrefix(context.locale)
        : singleUrlHelper.getPLP(context.locale);
    setTimeout(() => window.open(sourcePath + url, "_self"));
  }

  function goList(position: IPosition) {
    const { id, type, name } = position;
    if (id === HokkaidoId && type === HokkaidoType) {
      // hokkaido will skip pref list
      goList({
        id: "1",
        name,
        type: getNextType(EAreaType.prefecture),
      });
      return;
    }

    const changed = popupHelper.positionGo(position);
    const contentWrap = document.querySelector(`.${CONTAINER_CONTENT}`);
    const searchWrap = document.querySelector(`.${SEARCH_WRAP}`);
    if (!changed || !contentWrap || !searchWrap) return;

    // root has own container
    handleClassByCondition(
      contentWrap,
      CONTAINER_CONTENT_NOT_ROOT,
      id !== ROOT_ID
    );

    // search show depend
    handleClassByCondition(
      searchWrap,
      SEARCH_WRAP_SHOW,
      SHOW_SEARCH_TYPE.includes(type)
    );

    popupHelper.clearSelected();

    renderSearchCount();
    renderBreadcrumb();
    renderList({
      eventPayloadType: type,
      eventPayloadId: id,
    });
  }

  function clearState() {
    popupHelper.clearSelected();
    goList({
      id: ROOT_ID,
      name: context.intl.formatMessage(messages.allJapan),
      type: EAreaType.region,
    });
  }

  function renderBreadcrumb() {
    const containerEle = document.querySelector(
      ".container-search-popup-list-title"
    );
    if (!containerEle) return;
    const positionList = popupHelper.getPositionArray();
    const fragment = new DocumentFragment();

    for (let i = 0; i < positionList.length; i++) {
      const bread = positionList[i];
      const breadEle = document.createElement("li");
      breadEle.dataset.resourceType =
        AreaValueToTypeMap[getLastType(bread.type)];
      breadEle.dataset.resourceId = bread.id;

      breadEle.dataset.eventPayloadId = bread.id;
      breadEle.dataset.eventPayloadType = bread.type;
      breadEle.dataset.eventPayloadName = bread.name;
      breadEle.dataset.event = EEventType.go;
      breadEle.textContent = bread.name;
      fragment.append(breadEle);
    }
    containerEle.replaceChildren(fragment);
  }

  function renderList({
    eventPayloadType: type,
    eventPayloadId: id,
  }: Pick<ISearchPopupDataset, "eventPayloadType" | "eventPayloadId">) {
    const areaData = searchDataHelper.getAreaAndLineData();
    if (!areaData || !id || !type) return;
    let list, containerEle, overwriteWrapUl;
    switch (type) {
      case EAreaType.region:
        list = areaData.regionList;
        containerEle = document.querySelector(`.${ROOT_LIST_CONTAINER}`);
        break;
      case EAreaType.prefecture:
        list = areaData.rIdPrefectureMap[id];
        containerEle = document.querySelector(`.${NODE_LIST_CONTAINER}`);
        break;
      case EAreaType.line:
        list = areaData.pIdLinesMap[id];
        containerEle = document.querySelector(`.${NODE_LIST_CONTAINER}`);
        overwriteWrapUl = renderCheckboxButtonUnit(list);
        break;
      case EAreaType.station:
        list = areaData.pIdAndLIdStationsMap[id];
        containerEle = document.querySelector(`.${NODE_LIST_CONTAINER}`);
        overwriteWrapUl = renderCheckboxUnit(list);
        break;
      case EAreaType.city:
        return renderCity(id);
      default:
        return;
    }

    if (!list || list.length <= 0 || !containerEle) return;

    const wrapUl = overwriteWrapUl ? overwriteWrapUl : renderButtonUnit(list);
    containerEle.replaceChildren(wrapUl);
  }
  function getLastType(type: EAreaType) {
    if (!type) return type;
    const globalType = popupHelper.type;
    if (globalType === ESearchPopupType.trainLine && type === EAreaType.line) {
      return String(Number(type) - 2) as EAreaType;
    }
    return String(Number(type) - 1) as EAreaType;
  }

  function getNextType(type: EAreaType) {
    if (!type) return type;
    const globalType = popupHelper.type;
    // Hokkaido sp
    if (type === EAreaType.prefecture) {
      return globalType === ESearchPopupType.trainLine
        ? EAreaType.line
        : EAreaType.city;
    }
    return String(Number(type) + 1) as EAreaType;
  }

  function renderSearchCount() {
    const countEle = document.querySelector(
      ".container-search-popup-search-count"
    );
    const searchButton = document.querySelector(
      ".container-search-popup-search"
    ) as HTMLButtonElement;
    if (!countEle) return;
    const selectedValues = popupHelper.getSelected().values();
    let current = selectedValues.next().value;
    let selectedCountSum = 0;
    while (current) {
      selectedCountSum += Number(current.count);
      current = selectedValues.next().value;
    }

    if (selectedCountSum > 0) {
      searchButton.disabled = false;
      const count = `(${selectedCountSum})`;
      countEle.textContent = count;
    } else {
      countEle.textContent = "";
      searchButton.disabled = true;
    }
  }

  function renderCity(id: string) {
    const containerEle = document.querySelector(`.${NODE_LIST_CONTAINER}`);
    const cityCompositeList = searchDataHelper.getRenderCityListByPid(id);
    if (!containerEle || cityCompositeList.length <= 0) return;
    let childList;
    if (cityCompositeList.length === 1) {
      childList = renderCheckboxUnit(cityCompositeList[0].list);
    } else {
      const fragment = new DocumentFragment();
      for (const item of cityCompositeList) {
        const wrap = document.createElement("div");
        wrap.classList.add("container-search-popup-detail-leaf");
        wrap.dataset.eventPayloadId = item.groupId;
        const title = document.createElement("dt");
        title.classList.add("container-search-popup-detail-title");
        title.textContent = item.groupName;
        const list = renderCheckboxUnit(item.list);
        wrap.append(title);
        wrap.append(list);
        fragment.append(wrap);
      }

      childList = fragment;
    }

    containerEle.replaceChildren(childList);
  }

  function renderCheckboxUnit(list: IRCity[] | IRStation[]) {
    const fragment = new DocumentFragment();
    const wrapUl = document.createElement("ul");
    wrapUl.classList.add("container-search-popup-list");
    wrapUl.classList.add("container-search-popup-leaf-list");
    for (const item of list) {
      if (!item.count) continue;
      const li = document.createElement("li");
      const count = String(item.count ?? 0);
      const label = document.createElement("label");
      const checkbox = document.createElement("input");
      checkbox.type = "checkbox";
      const span = document.createElement("span");
      const b = document.createElement("b");

      b.textContent = count;
      let name = item.name ?? "";
      if ("dtl" in item) {
        name = mergeCityGroupName(name, item.dtl);
      }
      span.textContent = name;

      checkbox.dataset.eventPayloadCount = count;
      checkbox.dataset.eventPayloadId = String(item.id ?? "");
      checkbox.dataset.eventPayloadUrl = item.url ?? "";
      checkbox.dataset.event = EEventType.select;

      checkbox.dataset.resourceType = item.type
        ? AreaValueToTypeMap[item.type]
        : "";
      checkbox.dataset.resourceId = String(item.id ?? "");

      span.append(b);
      label.append(checkbox);
      label.append(span);
      li.append(label);
      fragment.append(li);
    }
    wrapUl.append(fragment);
    return wrapUl;
  }

  function renderCheckboxButtonUnit(list: IRLine[]) {
    if (!list || list.length <= 0) return;
    const fragment = new DocumentFragment();
    const wrapUl = document.createElement("ul");
    wrapUl.classList.add("container-search-popup-list");
    for (const item of list) {
      if (!item.type || !item.count) continue;
      const li = document.createElement("li");
      const count = String(item.count ?? 0);

      const checkbox = document.createElement("input");
      checkbox.type = "checkbox";

      const button = document.createElement("button");
      const b = document.createElement("b");
      const span = document.createElement("span");
      b.textContent = count;
      span.textContent = item.name ?? "";
      button.dataset.eventPayloadId = String(item.combineId ?? "");
      // button.dataset.eventPayloadCombineId = String(item.combineId ?? "");
      button.dataset.eventPayloadType = getNextType(item.type);
      button.dataset.eventPayloadName = item.name ?? "";
      button.dataset.event = EEventType.go;
      button.append(span);
      button.append(b);

      checkbox.dataset.eventPayloadCount = count;
      checkbox.dataset.eventPayloadId = String(item.id ?? "");
      checkbox.dataset.eventPayloadUrl = item.url ?? "";
      checkbox.dataset.event = EEventType.select;

      checkbox.dataset.resourceType = AreaValueToTypeMap[item.type];
      checkbox.dataset.resourceId = String(item.id ?? "");

      li.append(checkbox);
      li.append(button);
      fragment.append(li);
    }
    wrapUl.append(fragment);
    return wrapUl;
  }

  function renderButtonUnit(list: IRPrefecture[] | IRRegion[]) {
    const wrapUl = document.createElement("ul");
    wrapUl.classList.add("container-search-popup-list");

    const fragment = new DocumentFragment();
    for (const item of list) {
      if (!item.type || !item.count) continue;
      const li = document.createElement("li");
      const count = String(item.count ?? 0);

      const button = document.createElement("button");
      const b = document.createElement("b");
      const span = document.createElement("span");
      b.textContent = count;
      span.textContent = item.name ?? "";

      button.dataset.eventPayloadCount = count;
      button.dataset.eventPayloadId = String(item.id ?? "");
      button.dataset.eventPayloadType = getNextType(item.type);
      button.dataset.eventPayloadName = item.name ?? "";
      button.dataset.event = EEventType.go;
      button.append(span);
      button.append(b);
      li.append(button);
      fragment.append(li);
    }
    wrapUl.append(fragment);
    return wrapUl;
  }
}
